import { Css, StaticField, useSnackbar } from "@homebound/beam";
import { Observer } from "mobx-react";
import { UppyUploader } from "src/components/uploader";
import { DocumentType, SaveDocumentInput } from "src/generated/graphqlTypes";
import { BaseChangeEventFormState } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { useSaveDocumentsMutation } from "src/routes/projects/documents/DocumentsPage.generated";

type PreSignedContractUploaderProps<F> = { formState: BaseChangeEventFormState<F>; projectId: string };

/**
 * The signed contract that backs `hasPreSignedContract`: BOYL projects skip generating a new PandaDoc,
 * so the existing contract has to be attached here for the homeowner approval to be skipped.
 */
export function PreSignedContractUploader<F>(props: PreSignedContractUploaderProps<F>) {
  const { formState, projectId } = props;
  const { triggerNotice } = useSnackbar();
  const [saveDocuments] = useSaveDocumentsMutation();

  return (
    <Observer>
      {() => {
        if (!formState.hasPreSignedContract.value) return null;
        const documentName = formState.documentName.value;
        return (
          <div css={Css.df.fdc.gap2.$} data-testid="preSignedContract">
            <UppyUploader
              allowedFileTypes={["application/pdf"]}
              maxNumberOfFiles={1}
              type="DragDrop"
              // The drop zone defaults to 250px; span the form so it lines up with the fields above.
              dragDropWidth="100%"
              dragDropHeight={56}
              dragDropText="Drag & drop signed contract (PDF)"
              onFinish={async (uppyFile) => {
                const { meta, name, type, size } = uppyFile;
                const { s3Key } = meta;
                const input: SaveDocumentInput = {
                  parentId: projectId,
                  documentType: DocumentType.ChangeEvent,
                  name,
                  sizeInBytes: size,
                  asset: { contentType: type!, fileName: name, s3Key, sizeInBytes: size },
                };
                const { data } = await saveDocuments({ variables: { input } });
                const savedDocument = data?.saveDocuments.documents.first;
                if (!savedDocument) return triggerNotice({ message: `Failed to save ${name}` });
                formState.documentId.set(savedDocument.id);
                formState.documentName.set(savedDocument.name);
              }}
            />
            {documentName && <StaticField label="Attached Contract" labelStyle="left" value={documentName} />}
          </div>
        );
      }}
    </Observer>
  );
}
